import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {FormControl} from '@angular/forms';
import {Subject} from 'rxjs';
import {debounceTime, takeUntil} from 'rxjs/operators';
import {MatTableDataSource} from '@angular/material/table';
import {MatPaginator} from '@angular/material/paginator';
import {NationalDrugIntegrationDTO} from '../../../../models/national-drug/NationalDrugIntegrationDTO';
import {ExternalDrugDataService} from '../../../../services/external-drug-data.service';
import {SharedService} from '../../../../../common/services/shared-service';
import {Common} from '../../../../../common/util/Util';
import {EnvironmentService} from '../../../../../common/services/environment-service';

enum TableColumns {
  NappiCode = 'nappiCode',
  Name = 'name',
  DrugSchedule = 'drugSchedule',
  AtcLevel5Code = 'atcLevel5Code',
  StandardPacksize = 'standardPacksize',
  SingleExitPrice = 'singleExitPrice',
  SingleExitPriceIncVAT = 'singleExitPriceIncVAT',
  UnitPriceIncVAT = 'unitPriceIncVAT',
}

enum SearchBy {
  NappiCode = 'NAPPI Code',
  AtcCode = 'ATC Code',
}

@Component({
  selector: 'app-external-drug-data',
  templateUrl: './external-drug-data.component.html',
  styleUrls: [
    '../../../../../../../styles/global/_management-styles.scss'
  ],
})
export class ExternalDrugDataComponent implements OnInit, OnDestroy {
  readonly destroyed$: Subject<boolean> = new Subject();
  readonly debounceTime = Common.HALF_SECOND_DEBOUNCE;
  readonly emptyString = Common.EMPTY_VALUE;
  readonly tableColumns = TableColumns;
  readonly searchByOptionTextChanged: Subject<string> = new Subject<string>();

  selectedDrugSearchByOption: string;
  searchByOptions: string[] = [...Object.values(SearchBy)];
  searchValueFormControl: FormControl;
  displayedColumns: TableColumns[] = Object.values<TableColumns>(TableColumns);
  dataSource: MatTableDataSource<NationalDrugIntegrationDTO> = new MatTableDataSource<NationalDrugIntegrationDTO>();
  length = 100;
  pageSize = 10;
  pageNumber = 0;
  pageSizeOptions: number[] = [5, 10, 25, 100];

  @ViewChild(MatPaginator, {static: false}) paginator: MatPaginator;
  constructor(
    private drugExternalDataService: ExternalDrugDataService,
    private environmentService: EnvironmentService,
    private sharedService: SharedService) {
    this.searchValueFormControl = new FormControl(this.emptyString);
  }

  ngOnInit(): void {
    this.initSubscriptions();
    this.getListOfExternalDrugData();
  }

  initSubscriptions(): void {
    this.searchByOptionTextChanged
      .pipe(
        debounceTime(this.debounceTime),
        takeUntil(this.destroyed$),
      ).subscribe({
      next: (_: string) => {
        this.getListOfExternalDrugData();
      }
    });
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  onSearchBySelectionChanged(value: string): void {
    this.selectedDrugSearchByOption = value;
    this.onInputChangedSearchCase(this.emptyString);
  }

  onInputChangedSearchCase(value: string): void {
    this.searchValueFormControl.patchValue(value?.trim());
    this.searchByOptionTextChanged.next(value?.trim());
  }

  private getListOfExternalDrugData(): void {
    let searchMethod: string;
    const searchValue = this.searchValueFormControl.value;

    switch (this.selectedDrugSearchByOption) {
    case SearchBy.NappiCode:
      searchMethod = 'getListOfProductsByNappiCode';
      break;
    case SearchBy.AtcCode:
      searchMethod = 'getListOfProductsByAtcCode';
      break;
    default:
      searchMethod = 'getListOfProductsByNappiCode';
    }

    this.drugExternalDataService[searchMethod](searchValue)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (response: NationalDrugIntegrationDTO[]) => {
          this.calculateVATPrices(response);
          this.dataSource.data = response;
          this.dataSource.paginator = this.paginator;
        },
        error: (errorResponse) => {
          this.sharedService.openErrorDialog(errorResponse.error.businessMessage);
        }
      });
  }

  private calculateVATPrices(nationalDrugIntegrationDTO: NationalDrugIntegrationDTO[]) {
    nationalDrugIntegrationDTO.forEach((drug: NationalDrugIntegrationDTO) => {
      const vatPercentage = this.environmentService.getVatPercentage() / 100;
      drug.singleExitPriceIncVAT = drug.singleExitPrice + (drug.singleExitPrice * vatPercentage);
      drug.unitPriceIncVAT = drug.singleExitPriceIncVAT / drug.standardPacksize;
    });
  }

}
